import classnames from "classnames";
import React, { useCallback, useRef, useState } from "react";
import Slider from "react-slick";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import Image from "web/react/components/product-gallery/product-gallery-image/product-gallery-image";
import MoreProducts from "web/react/components/product-gallery/product-gallery-more-products/product-gallery-more-products";
import analytics from "web/script/analytics/analytics";
import { useLeadHref } from "web/react/components/lead-link/lead-link";
import { BREAKPOINTS } from "web/react/constants";
import "web/react/components/product-gallery/slick-theme.less";
import "web/react/components/product-gallery/slick.less";
import styles from "./product-gallery-carousel.css";

interface ProductGalleryCarouselProps {
    analyticsAction: string;
    settings: object;
    images: ProductImage[];
    hasRelatedProductsSidebar: boolean;
    relatedProducts?: ProductCard[];
    handleImageClick?: (index: number) => void;
    openFullscreenGallery?: (index: number) => void;
    handleFirstInteraction?: () => void;
    dotsInsideGallery?: boolean;
    firstInteraction?: boolean;
    isFullscreen?: boolean;
    setCurrentSlideIndex?: () => void;
    imageType?: string;
    currentLinkId?: string;
    swipeRedirect?: boolean;
    variant?: "oos-carousel";
}

let firstClientX;

// Component for the gallery thumbnail image so we can lazy-load it client-side instead
function ThumbnailImage({ image }: { image: string }): React.ReactElement | null {
    const [src, setSrc] = React.useState(
        "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
    );

    React.useEffect(() => {
        if (window.innerWidth >= BREAKPOINTS.Medium) {
            setSrc(image);
        }
    }, [image]);

    if (window.innerWidth >= BREAKPOINTS.Medium) {
        return <img src={src} width="48" height="48" />;
    } else {
        return null;
    }
}

interface ArrowProps {
    className?: string; // react-slick passed
    onClick?: () => void; // react-slick passed
}

function Arrow({ className, onClick }: ArrowProps): JSX.Element {
    return (
        <button type="button" className={classnames(className, styles.arrow)} onClick={onClick}>
            <SVGIcon name="chevron" />
        </button>
    );
}

function ProductGalleryCarousel({
    analyticsAction,
    firstInteraction = true,
    handleFirstInteraction,
    images,
    isFullscreen = false,
    relatedProducts,
    handleImageClick,
    openFullscreenGallery,
    settings,
    currentLinkId,
    swipeRedirect,
    hasRelatedProductsSidebar,
    variant,
}: ProductGalleryCarouselProps): React.ReactElement {
    let lastImageIndex = images.length - 1;
    const oosCarouselVariant = variant === "oos-carousel";

    function onImageClick(index): void {
        if (isFullscreen) {
            return;
        }
        handleImageClick && handleImageClick(index);
    }

    const galleryImages = images.map((image, index) => {
        return (
            /* hack to get slick-slider ssr to set slick-track width to NaN (ignored by the
             * browser) instead of 0, which causes the slides to start in the wrong place until
             * the client version is initialized*/
            <div key={image.large_url} style={{}}>
                <Image
                    className={classnames({
                        [styles.stretchImage]: oosCarouselVariant && !isFullscreen,
                    })}
                    isFullscreen={isFullscreen}
                    onClick={(): void => onImageClick(index)}
                    image={image}
                    isSingle={images.length === 1}
                    hasRelatedProductsSidebar={hasRelatedProductsSidebar}
                />
            </div>
        );
    });

    const galleryElements =
        window.innerWidth < BREAKPOINTS.Medium && relatedProducts && relatedProducts.length > 0
            ? [
                  ...galleryImages,
                  <MoreProducts
                      key="related-slide"
                      products={relatedProducts}
                      fullscreen={isFullscreen}
                  />,
              ]
            : galleryImages;
    const [seenAll, setSeenAll] = useState<boolean>(false);
    const [relatedSeen, setRelatedSeen] = useState<boolean>(false);
    const [imagesSeen, setImagesSeen] = useState<number>(0);
    const [interactionCount, setInteractionCount] = useState<number>(0);
    const leadUrl = useLeadHref("/track/lead/" + currentLinkId, "redirect_on_swipe");

    const sliderRef = useRef<any>(null);

    const handleTouchStart = useCallback((event) => {
        if (event && event.touches.length > 0) {
            firstClientX = event.touches[0].clientX;
        }
    }, []);

    let handleTouchMoved = useCallback((event) => {
        if (event.touches.length > 0) {
            const minValue = 20;
            const clientX = event.touches[0].clientX - firstClientX;

            // Disable vertical scrolling when user swipes horizontally (Safari fix).
            if (Math.abs(clientX) > minValue) {
                event.preventDefault();
                event.returnValue = false;
                return false;
            }
        }
    }, []);

    const sliderElement = useCallback(
        (node) => {
            if (node === null) {
                return;
            }
            node.addEventListener("touchmove", handleTouchMoved, {
                passive: false,
            });
            node.addEventListener("gesturestart", (event) => event.preventDefault());
        },
        [handleTouchMoved]
    );

    function getDots(dots): React.ReactElement {
        if (window.innerWidth < BREAKPOINTS.Medium) {
            return (
                <div>
                    <ul>{dots}</ul>
                </div>
            );
        } else {
            if (oosCarouselVariant && !isFullscreen) {
                return <React.Fragment />; // we must use a Fragment because slick-slider uses React.cloneElement() unsafely
            }

            const thumbnails = dots.map((dot, index) => {
                const image: ProductImage | undefined = images[index];

                if (!image) {
                    return null;
                }

                return (
                    <li key={dot.key} className={dot.props.className}>
                        <button onClick={dot.props.children.props.onClick}>
                            <ThumbnailImage image={image.thumbnail_url} />
                        </button>
                    </li>
                );
            });

            return <ul>{thumbnails}</ul>;
        }
    }

    function onSlideChange(currentSlide, nextSlide): void {
        if (!firstInteraction && handleFirstInteraction) {
            handleFirstInteraction();
        }

        analytics.event("engagement", analyticsAction, "changed_image");

        if (swipeRedirect && interactionCount != null) {
            setInteractionCount(interactionCount + 1);
        }

        if (!seenAll) {
            let tempImagesSeen = imagesSeen;
            if (nextSlide === currentSlide + 1) {
                // User scrolls right
                tempImagesSeen += 1;
            } else if (nextSlide === currentSlide - 1 || nextSlide === lastImageIndex) {
                // User scrolls left
                tempImagesSeen -= 1;
            }

            if (Math.abs(tempImagesSeen) === lastImageIndex) {
                setSeenAll(true);
                analytics.event("engagement", analyticsAction, "seen_all");
            } else {
                setImagesSeen(tempImagesSeen);
            }
        }

        if (relatedProducts && !relatedSeen && nextSlide === lastImageIndex + 1) {
            setRelatedSeen(true);
            analytics.event("engagement", analyticsAction, "seen_related_products_slide");
        }

        // pmt_221 - after swiping twice we load the partner page, this is a onetime action
        if (interactionCount >= 1 && currentLinkId) {
            setInteractionCount(0);
            analytics.event("engagement", "redirect_on_swipe");
            window.open(leadUrl, "_blank");
        }
    }

    function handleOnSwipe(): void {
        if (sliderRef) {
            sliderRef.current.innerSlider.clickable = true;
        }
    }

    const sliderClasses = classnames(
        { [styles.carouselImage]: !isFullscreen },
        // eslint-disable-next-line css-modules/no-undef-class
        { [styles.fullscreen]: isFullscreen },
        // eslint-disable-next-line css-modules/no-undef-class
        { [styles.singleImageGallery]: images.length === 1 }
    );

    const sliderWrapperClasses = classnames(
        { [styles.carousel]: !isFullscreen && !oosCarouselVariant },
        { [styles.oosCarouselVariant]: !isFullscreen && oosCarouselVariant }
    );

    const oosCarouselArrows =
        oosCarouselVariant && !isFullscreen
            ? {
                  prevArrow: <React.Fragment />, // we must use a Fragment because slick-slider uses React.cloneElement() unsafely
                  nextArrow: <Arrow />,
              }
            : {};

    return (
        <div ref={sliderElement} onTouchStart={handleTouchStart} className={sliderWrapperClasses}>
            <Slider
                ref={sliderRef}
                className={sliderClasses}
                beforeChange={onSlideChange}
                appendDots={(dots): React.ReactElement => getDots(dots)}
                {...oosCarouselArrows}
                {...settings}
                // Workaround for click bug
                // https://github.com/akiran/react-slick/issues/604#issuecomment-429166338
                onSwipe={handleOnSwipe}
            >
                {galleryElements}
            </Slider>

            {!isFullscreen && !oosCarouselVariant && (
                <button
                    onClick={(): void =>
                        openFullscreenGallery &&
                        openFullscreenGallery(sliderRef.current.innerSlider.state.currentSlide)
                    }
                    className={styles.expandButton}
                >
                    <SVGIcon name="expand" />
                </button>
            )}
        </div>
    );
}

export default ProductGalleryCarousel;
